/** @jsxImportSource @emotion/react */
import { Component, ErrorInfo, ReactNode } from 'react'
import { css } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

/**
 * Error Boundary for Scheduler Wizard
 * Catches JavaScript errors in child component tree and displays fallback UI
 */
class SchedulerErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to monitoring service in production
    if (process.env.NODE_ENV === 'production') {
      // TODO: Send to error monitoring service
    }
    console.error('SchedulerWizard Error:', error, errorInfo)
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null })
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 400px;
            padding: 40px;
            background-color: ${colors.SECONDARY_400};
            border-radius: 8px;
            text-align: center;
          `}
        >
          <div
            css={css`
              width: 64px;
              height: 64px;
              margin-bottom: 24px;
              display: flex;
              align-items: center;
              justify-content: center;
              background-color: ${colors.ERROR_300}1A;
              border-radius: 50%;
            `}
          >
            <svg
              width='32'
              height='32'
              viewBox='0 0 24 24'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z'
                fill={colors.ERROR_300}
              />
            </svg>
          </div>
          <h2
            css={css`
              font-family: 'Barlow Condensed', sans-serif;
              font-size: 24px;
              font-weight: 600;
              color: ${colors.WHITE};
              margin: 0 0 8px 0;
            `}
          >
            Something went wrong
          </h2>
          <p
            css={css`
              font-family: 'Inter', sans-serif;
              font-size: 14px;
              color: ${colors.NEUTRAL_50};
              margin: 0 0 24px 0;
              max-width: 400px;
            `}
          >
            We encountered an error while loading the scheduler. Please try
            again or contact support if the issue persists.
          </p>
          <button
            type='button'
            onClick={this.handleRetry}
            css={css`
              padding: 8px 16px;
              background-color: ${colors.PRIMARY_300};
              border: none;
              color: ${colors.SECONDARY_300};
              font-family: 'Inter', sans-serif;
              font-size: 16px;
              font-weight: 500;
              border-radius: 8px;
              cursor: pointer;
              transition: background-color 0.2s ease;

              &:hover {
                background-color: ${colors.PRIMARY_200};
              }
            `}
          >
            Try Again
          </button>
        </div>
      )
    }

    return this.props.children
  }
}

export default SchedulerErrorBoundary
